import { createMutation } from '@magicdoorinc/ui';
import { createMemo, createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { ManualReconciliationsRepository } from '~/repositories/manualReconciliationsRepository';
import { ManualReconciliationEntryType } from '~/swagger/accounting/Api';
import { debounce } from '~/utils/tool';
import type {
  AddManualReconciliationEntriesDto,
  CancelManualReconciliationDto,
  CompleteManualReconciliationDto,
  ManualReconciliationCandidateDto,
  ManualReconciliationDetailDto,
  ManualReconciliationDepositSlipDto,
  ManualReconciliationDto,
  ManualReconciliationsDto,
  RemoveManualReconciliationEntriesDto,
  StartManualReconciliationDto,
  UpdateManualReconciliationDto,
} from '~/swagger/accounting/Api';

type ReconciliationSummaryLiveFields = Pick<
  ManualReconciliationDetailDto,
  | 'opening'
  | 'additions'
  | 'subtractions'
  | 'closing'
  | 'status'
  | 'memo'
  | 'actualAdditions'
  | 'actualSubtractions'
  | 'actualClosing'
  | 'additionsDifference'
  | 'subtractionsDifference'
  | 'closingDifference'
>;

const pickReconciliationSummaryLiveFields = (dto: ManualReconciliationDetailDto): ReconciliationSummaryLiveFields => ({
  opening: dto.opening,
  additions: dto.additions,
  subtractions: dto.subtractions,
  closing: dto.closing,
  status: dto.status,
  memo: dto.memo,
  actualAdditions: dto.actualAdditions,
  actualSubtractions: dto.actualSubtractions,
  actualClosing: dto.actualClosing,
  additionsDifference: dto.additionsDifference,
  subtractionsDifference: dto.subtractionsDifference,
  closingDifference: dto.closingDifference,
});

const reconciliationCandidateMatchesIdSets = (
  c: ManualReconciliationCandidateDto,
  transactionIds: ReadonlySet<string> | undefined,
  depositSlipIds: ReadonlySet<string> | undefined
): boolean => Boolean(transactionIds?.has(c.transactionId)) || Boolean(c.depositSlipId && depositSlipIds?.has(c.depositSlipId));

const idSetOrUndefined = (ids: readonly string[] | undefined): ReadonlySet<string> | undefined =>
  ids && ids.length > 0 ? new Set(ids) : undefined;

export const [BankAccountReconciliationProvider, useBankAccountReconciliation] = createMagicDoorContext('BankAccountReconciliation', () => {
  const repo = new ManualReconciliationsRepository();

  const [reconciliations, setReconciliations] = createSignal<ManualReconciliationsDto | undefined>(undefined);
  const [currentReconciliation, setCurrentReconciliation] = createSignal<ManualReconciliationDetailDto | undefined>(undefined);
  const [lastReconciliation, setLastReconciliation] = createSignal<ManualReconciliationDto | undefined>(undefined);
  const [reconciliationCandidates, setReconciliationCandidates] = createSignal<ManualReconciliationCandidateDto[] | undefined>(undefined);
  const [reconciliationDepositSlips, setReconciliationDepositSlips] = createSignal<ManualReconciliationDepositSlipDto[] | undefined>(
    undefined
  );
  const [isReconciliationsLoading, setIsReconciliationsLoading] = createSignal<boolean>(false);
  const [isReconciliationDetailsLoading, setIsReconciliationDetailsLoading] = createSignal<boolean>(false);
  const [isReconciliationCandidatesLoading, setIsReconciliationCandidatesLoading] = createSignal<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = createSignal<boolean>(false);
  const [reconciliationCandidatesRequestId, setReconciliationCandidatesRequestId] = createSignal<string | undefined>(undefined);
  const [reconciliationCandidatesResolvedId, setReconciliationCandidatesResolvedId] = createSignal<string | undefined>(undefined);
  const [error, setError] = createSignal<Error | undefined>(undefined);
  const [bankAccountId, setBankAccountId] = createSignal<string | undefined>(undefined);
  const [reconciliationId, setReconciliationId] = createSignal<string | undefined>(undefined);
  const [reconciliationSummaryLive, setReconciliationSummaryLive] = createSignal<ReconciliationSummaryLiveFields | undefined>(undefined);
  const [reconciliationSelectionSyncBusy, setReconciliationSelectionSyncBusy] = createSignal(false);
  let reconciliationRequestSequence = 0;
  let reconciliationCandidatesRequestSequence = 0;

  const reconciliationSummaryTotals = createMemo((): ReconciliationSummaryLiveFields | undefined => {
    const base = currentReconciliation();
    const live = reconciliationSummaryLive();
    if (!base) return undefined;
    const baseSummary = pickReconciliationSummaryLiveFields(base);
    if (!live) return baseSummary;
    return { ...baseSummary, ...live };
  });

  const applyDetailResponse = (reconciliationId: string, result: ManualReconciliationDetailDto) => {
    if (currentReconciliation()?.id !== reconciliationId) return;
    setCurrentReconciliation(result);
    setReconciliationSummaryLive(undefined);
  };

  const fetchReconciliations = createMutation(async (bankAccountId: string) => {
    if (!bankAccountId) return;
    try {
      setIsReconciliationsLoading(true);
      setError(undefined);
      const data = await repo.list(bankAccountId);
      setReconciliations(data);
      setBankAccountId(bankAccountId);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsReconciliationsLoading(false);
    }
  });

  const fetchReconciliation = async (bankAccountId: string, reconciliationId: string) => {
    if (!bankAccountId || !reconciliationId) return;
    const requestSequence = ++reconciliationRequestSequence;
    try {
      setIsReconciliationDetailsLoading(true);
      setIsInitialLoading(true);
      setIsReconciliationCandidatesLoading(false);
      setCurrentReconciliation(undefined);
      setReconciliationSummaryLive(undefined);
      setReconciliationCandidates(undefined);
      setReconciliationDepositSlips(undefined);
      setReconciliationCandidatesRequestId(undefined);
      setReconciliationCandidatesResolvedId(undefined);
      setError(undefined);
      const data = await repo.detail(bankAccountId, reconciliationId);
      if (requestSequence !== reconciliationRequestSequence) return;
      setCurrentReconciliation(data);
      setReconciliationSummaryLive(undefined);
      setBankAccountId(bankAccountId);
      setReconciliationId(reconciliationId);
      return data;
    } catch (err) {
      if (requestSequence !== reconciliationRequestSequence) return;
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      if (requestSequence === reconciliationRequestSequence) {
        setIsReconciliationDetailsLoading(false);
        setIsInitialLoading(false);
      }
    }
  };

  const fetchLastReconciliation = createMutation(async (bankAccountId: string) => {
    if (!bankAccountId) return;
    try {
      setError(undefined);
      const data = await repo.last(bankAccountId);
      setLastReconciliation(data);
      setBankAccountId(bankAccountId);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  });

  const startReconciliation = createMutation(async (bankAccountId: string, reconciliation: StartManualReconciliationDto) => {
    try {
      setError(undefined);
      const result = await repo.start(bankAccountId, reconciliation);
      await fetchReconciliations(bankAccountId);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  });

  const completeReconciliation = createMutation(
    async (bankAccountId: string, reconciliationId: string, payload: CompleteManualReconciliationDto) => {
      try {
        setError(undefined);
        const result = await repo.complete(bankAccountId, reconciliationId, payload);
        applyDetailResponse(reconciliationId, result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      }
    }
  );

  const cancelReconciliation = createMutation(
    async (bankAccountId: string, reconciliationId: string, payload: CancelManualReconciliationDto) => {
      try {
        setError(undefined);
        const result = await repo.cancel(bankAccountId, reconciliationId, payload);
        applyDetailResponse(reconciliationId, result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      }
    }
  );

  const addTransactionsToReconciliation = createMutation(
    async (bankAccountId: string, reconciliationId: string, entry: AddManualReconciliationEntriesDto) => {
      try {
        setError(undefined);
        const result = await repo.addEntries(bankAccountId, reconciliationId, entry);
        if (currentReconciliation()?.id === reconciliationId) {
          setCurrentReconciliation(result);
          setReconciliationSummaryLive(undefined);
          fetchReconciliationCandidates(bankAccountId, reconciliationId, { mode: 'refresh' });
        }
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      }
    }
  );

  const removeTransactionsFromReconciliation = createMutation(
    async (bankAccountId: string, reconciliationId: string, data: RemoveManualReconciliationEntriesDto) => {
      try {
        setError(undefined);
        const result = await repo.removeEntries(bankAccountId, reconciliationId, data);
        if (currentReconciliation()?.id === reconciliationId) {
          setCurrentReconciliation(result);
          setReconciliationSummaryLive(undefined);
          fetchReconciliationCandidates(bankAccountId, reconciliationId, { mode: 'refresh' });
        }
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      }
    }
  );

  const updateReconciliation = createMutation(
    async (bankAccountId: string, reconciliationId: string, data: UpdateManualReconciliationDto) => {
      try {
        setError(undefined);
        const result = await repo.update(bankAccountId, reconciliationId, data);
        applyDetailResponse(reconciliationId, result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      }
    }
  );

  const deleteReconciliation = createMutation(async (bankAccountId: string, reconciliationId: string) => {
    try {
      setError(undefined);
      await repo.delete(bankAccountId, reconciliationId);
      if (currentReconciliation()?.id === reconciliationId) {
        clearCurrentReconciliation();
      }
      await fetchReconciliations(bankAccountId);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  });

  const fetchReconciliationCandidatesCore = async (
    bankAccountId: string,
    reconciliationId: string,
    mode: 'initial' | 'refresh' = 'refresh'
  ) => {
    if (!bankAccountId || !reconciliationId) return;

    const requestSequence = ++reconciliationCandidatesRequestSequence;

    try {
      if (mode === 'initial') {
        setIsReconciliationCandidatesLoading(true);
      }
      setReconciliationCandidatesRequestId(reconciliationId);
      if (mode === 'initial') {
        setReconciliationCandidatesResolvedId(undefined);
        setReconciliationCandidates(undefined);
        setReconciliationDepositSlips(undefined);
      }
      setError(undefined);

      const data = await repo.candidates(bankAccountId, reconciliationId);

      if (requestSequence !== reconciliationCandidatesRequestSequence) return;

      setReconciliationCandidates(data.candidates);
      setReconciliationDepositSlips(data.depositSlips);
      setReconciliationCandidatesResolvedId(reconciliationId);
      return data;
    } catch (err) {
      if (requestSequence !== reconciliationCandidatesRequestSequence) return;

      console.error('Error fetching reconciliation candidates:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setReconciliationCandidates([]);
      setReconciliationDepositSlips([]);
      setReconciliationCandidatesResolvedId(reconciliationId);
      throw err;
    } finally {
      if (mode === 'initial' && requestSequence === reconciliationCandidatesRequestSequence) {
        setIsReconciliationCandidatesLoading(false);
      }
    }
  };

  const fetchReconciliationCandidatesDebounced = debounce(
    (bankAccountId: string, reconciliationId: string) => fetchReconciliationCandidatesCore(bankAccountId, reconciliationId, 'refresh'),
    500
  );

  const applyOptimisticCandidatePatchesForSelection = (
    reconciliationId: string,
    removeFirst: RemoveManualReconciliationEntriesDto | undefined,
    addSecond: AddManualReconciliationEntriesDto | undefined
  ) => {
    if (currentReconciliation()?.id !== reconciliationId) return;

    const txRemove = idSetOrUndefined(removeFirst?.transactionIds);
    const slipRemove = idSetOrUndefined(removeFirst?.depositSlipIds);
    const txAdd = idSetOrUndefined(addSecond?.transactionIds);
    const slipAdd = idSetOrUndefined(addSecond?.depositSlipIds);

    const doRemove = Boolean(txRemove || slipRemove);
    const doAdd = Boolean(txAdd || slipAdd);
    if (!doRemove && !doAdd) return;

    const addType = addSecond?.type ?? ManualReconciliationEntryType.Included;

    setReconciliationCandidates((prev) => {
      if (!prev) return prev;
      return prev.map((c) => {
        let next = c;
        if (doRemove && reconciliationCandidateMatchesIdSets(c, txRemove, slipRemove)) {
          next = { ...c, isSelected: false, selectedType: undefined };
        }
        if (doAdd && reconciliationCandidateMatchesIdSets(c, txAdd, slipAdd)) {
          next = { ...next, isSelected: true, selectedType: addType };
        }
        return next;
      });
    });
  };

  const revertOptimisticCandidatePatchesForSelection = (
    reconciliationId: string,
    appliedRemove: RemoveManualReconciliationEntriesDto | undefined,
    appliedAdd: AddManualReconciliationEntriesDto | undefined
  ) => {
    if (currentReconciliation()?.id !== reconciliationId) return;

    const txUndoAdd = idSetOrUndefined(appliedAdd?.transactionIds);
    const slipUndoAdd = idSetOrUndefined(appliedAdd?.depositSlipIds);
    const txUndoRemove = idSetOrUndefined(appliedRemove?.transactionIds);
    const slipUndoRemove = idSetOrUndefined(appliedRemove?.depositSlipIds);

    const undoAdd = Boolean(txUndoAdd || slipUndoAdd);
    const undoRemove = Boolean(txUndoRemove || slipUndoRemove);
    if (!undoAdd && !undoRemove) return;

    setReconciliationCandidates((prev) => {
      if (!prev) return prev;
      return prev.map((c) => {
        let next = c;
        if (undoAdd && reconciliationCandidateMatchesIdSets(c, txUndoAdd, slipUndoAdd)) {
          next = { ...c, isSelected: false, selectedType: undefined };
        }
        if (undoRemove && reconciliationCandidateMatchesIdSets(c, txUndoRemove, slipUndoRemove)) {
          next = { ...next, isSelected: true, selectedType: ManualReconciliationEntryType.Included };
        }
        return next;
      });
    });
  };

  const fetchReconciliationCandidates = (
    bankAccountId: string = currentReconciliation()?.bankAccountId ?? '',
    reconciliationId: string = currentReconciliation()?.id ?? '',
    options?: { mode?: 'initial' | 'refresh' }
  ) => {
    if (!bankAccountId || !reconciliationId) return;

    if (options?.mode === 'initial') {
      return fetchReconciliationCandidatesCore(bankAccountId, reconciliationId, 'initial');
    }

    return fetchReconciliationCandidatesDebounced(bankAccountId, reconciliationId);
  };

  const clearCurrentReconciliation = () => {
    setCurrentReconciliation(undefined);
    setReconciliationSummaryLive(undefined);
    setReconciliationSelectionSyncBusy(false);
    setReconciliationId(undefined);
    setReconciliationCandidates(undefined);
    setReconciliationDepositSlips(undefined);
    setReconciliationCandidatesRequestId(undefined);
    setReconciliationCandidatesResolvedId(undefined);
    setIsReconciliationDetailsLoading(false);
    setIsInitialLoading(false);
    setIsReconciliationCandidatesLoading(false);
  };

  const clearState = () => {
    setReconciliations(undefined);
    setCurrentReconciliation(undefined);
    setReconciliationSummaryLive(undefined);
    setReconciliationSelectionSyncBusy(false);
    setLastReconciliation(undefined);
    setReconciliationCandidates(undefined);
    setReconciliationDepositSlips(undefined);
    setBankAccountId(undefined);
    setReconciliationId(undefined);
    setReconciliationCandidatesRequestId(undefined);
    setReconciliationCandidatesResolvedId(undefined);
    setIsReconciliationsLoading(false);
    setIsReconciliationDetailsLoading(false);
    setIsReconciliationCandidatesLoading(false);
    setIsInitialLoading(false);
    setError(undefined);
  };

  return {
    reconciliations,
    currentReconciliation,
    lastReconciliation,
    reconciliationCandidates,
    reconciliationDepositSlips,
    isReconciliationsLoading,
    isReconciliationDetailsLoading,
    isReconciliationCandidatesLoading,
    isInitialLoading,
    reconciliationCandidatesRequestId,
    reconciliationCandidatesResolvedId,
    error,
    bankAccountId,
    reconciliationId,
    reconciliationSummaryTotals,
    reconciliationSelectionSyncBusy,
    setReconciliationSelectionSyncBusy,

    fetchReconciliations,
    fetchReconciliation,
    fetchLastReconciliation,
    fetchReconciliationCandidates,
    startReconciliation,
    updateReconciliation,
    deleteReconciliation,
    completeReconciliation,
    cancelReconciliation,
    addTransactionsToReconciliation,
    removeTransactionsFromReconciliation,
    applyOptimisticCandidatePatchesForSelection,
    revertOptimisticCandidatePatchesForSelection,
    clearCurrentReconciliation,
    clearState,
  };
});
